import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DialogTitle } from '@onesource/components';
import {
  REVIEW_REMINDER_COOLDOWN_HOURS,
  REVIEW_REMINDER_NOTE_MAX,
  type TReviewReminderRecipient,
  type TReviewReminderResult,
} from '@onesource/schemas';
import { enqueueSnackbar } from 'notistack';
import { FC, useEffect, useMemo, useState } from 'react';
import { MdArrowBack, MdClose, MdNotificationsActive } from 'react-icons/md';

import { ReviewReminderDialogSkeleton } from './review-reminder-dialog-skeleton';
import { ReviewReminderFormStep, toFormOptions } from './review-reminder-form-step';
import { ReviewReminderPickList, reminderKey } from './review-reminder-pick-list';

/**
 * - students: bulk; pick one form, then tick the students.
 * - student-forms: one student; tick their forms.
 * (Teacher reminders have their own dialog, with form and review-time filters.)
 */
export type TReminderDialogMode = 'students' | 'student-forms';

interface ReviewReminderDialogProps {
  open: boolean;
  onClose: () => void;
  mode: TReminderDialogMode;
  title: string;
  /** One line on what is sent and where the link goes. */
  intro: string;
  /** Each line in the list: who or what, then the detail. */
  label: (recipient: TReviewReminderRecipient) => { primary: string; secondary: string };
  /** Everyone and everything that could be reminded (a dry run). */
  preview: () => Promise<TReviewReminderResult>;
  /** Send to the ticked ones. */
  send: (picked: TReviewReminderRecipient[], note?: string) => Promise<TReviewReminderResult>;
  /** Which lines start ticked; default every one not reminded in the last 24 hours. */
  preselect?: (recipient: TReviewReminderRecipient) => boolean;
}

const plural = (count: number, noun: string) => `${count} ${count === 1 ? noun : `${noun}s`}`;
const defaultPicks = (
  items: TReviewReminderRecipient[],
  preselect: (recipient: TReviewReminderRecipient) => boolean = () => true,
) => new Set(items.filter(r => !r.skipped && preselect(r)).map(reminderKey));

/**
 * Remind people after a preview: pick (a form first, for a bulk student reminder),
 * then tick or untick who or what gets it. Anyone reminded about the same thing in
 * the last 24 hours shows as already reminded and is not sent again.
 */
export const ReviewReminderDialog: FC<ReviewReminderDialogProps> = ({
  open,
  onClose,
  mode,
  title,
  intro,
  label,
  preview,
  send,
  preselect,
}) => {
  const [result, setResult] = useState<TReviewReminderResult | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [isSending, setIsSending] = useState(false);
  const pickFormFirst = mode === 'students';

  useEffect(() => {
    if (!open) {
      return;
    }
    setResult(null);
    setErrorText(null);
    setFormId(null);
    setNote('');
    preview()
      .then(next => {
        setResult(next);
        // One form only? Skip the form step.
        const forms = pickFormFirst ? toFormOptions(next.recipients) : [];
        const onlyForm = forms.length === 1 ? forms[0].formId : null;
        setFormId(onlyForm);
        setSelected(
          defaultPicks(
            pickFormFirst ? next.recipients.filter(r => r.form?.formId === onlyForm) : next.recipients,
            preselect,
          ),
        );
      })
      .catch((error: { message?: string }) => setErrorText(error?.message ?? 'Could not load who to remind'));
    // Only on open: `preview` is a new function each render of the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isFormStep = pickFormFirst && !formId;
  const items = useMemo(
    () =>
      result ? (pickFormFirst ? result.recipients.filter(r => r.form?.formId === formId) : result.recipients) : [],
    [result, pickFormFirst, formId],
  );
  const picked = items.filter(r => !r.skipped && selected.has(reminderKey(r)));
  const skipped = items.filter(r => r.skipped).length;
  const chosenForm = pickFormFirst && formId ? items[0]?.form : null;
  const isNoteTooLong = note.length > REVIEW_REMINDER_NOTE_MAX;
  const actionLabel = (count: number) => `Send ${plural(count, 'reminder')}`;

  const chooseForm = (next: string) => {
    setFormId(next);
    setSelected(defaultPicks(result?.recipients.filter(r => r.form?.formId === next) ?? []));
  };

  const handleSend = async () => {
    setIsSending(true);
    try {
      const sent = await send(picked, note.trim() || undefined);
      enqueueSnackbar(`Sent ${plural(sent.sent, 'reminder')}`, { variant: 'success' });
      onClose();
    } catch (error) {
      setErrorText((error as { message?: string })?.message ?? 'Sending failed. Try again.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={isSending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle onClose={onClose} icon={<MdNotificationsActive size={20} />}>
        {chosenForm ? `${title}: ${chosenForm.formTitle}` : title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {intro}
        </Typography>

        {errorText && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorText}
          </Alert>
        )}

        {!result && !errorText ? (
          <ReviewReminderDialogSkeleton />
        ) : result && result.recipients.length === 0 ? (
          <Alert severity="info">Nobody to remind under these filters.</Alert>
        ) : result && isFormStep ? (
          <>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
              1. Pick the form to remind about
            </Typography>
            <ReviewReminderFormStep recipients={result.recipients} value={formId} onChange={chooseForm} />
          </>
        ) : result ? (
          <>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
              {pickFormFirst ? '2. Tick the students to remind' : 'Tick the forms to remind about'}
            </Typography>
            {skipped > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {skipped} already reminded in the last {REVIEW_REMINDER_COOLDOWN_HOURS} hours — not sent again.
              </Typography>
            )}
            <ReviewReminderPickList
              items={items}
              selected={selected}
              onChange={setSelected}
              label={label}
              showAvatar={mode !== 'student-forms'}
            />
            <TextField
              label="Note (optional)"
              placeholder="Added to the reminder"
              value={note}
              onChange={e => setNote(e.target.value)}
              multiline
              minRows={2}
              fullWidth
              error={isNoteTooLong}
              helperText={`${note.length}/${REVIEW_REMINDER_NOTE_MAX}`}
              sx={{ mt: 2 }}
            />
          </>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="soft" startIcon={<MdClose />} onClick={onClose} disabled={isSending}>
          Cancel
        </Button>
        {pickFormFirst && formId && result && toFormOptions(result.recipients).length > 1 && (
          <Button variant="outlined" startIcon={<MdArrowBack />} onClick={() => setFormId(null)} disabled={isSending}>
            Change form
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={isFormStep || picked.length === 0 || isSending || isNoteTooLong}
          startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : <MdNotificationsActive />}
        >
          {isFormStep ? 'Pick a form' : actionLabel(picked.length)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
